import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import PaystackBtn from "../paystack";

const scripts = {};
const interswitchCheckoutKeys = new Set();
let nextInterswitchComponentInstanceId = 1;

function recordInterswitchDiagnostic(payment, componentInstanceId, lifecycle) {
  const diagnostics = Array.isArray(window.__mbossPaymentDiagnostics)
    ? window.__mbossPaymentDiagnostics
    : [];
  const attemptId = payment?.attemptId == null ? null : String(payment.attemptId);
  const providerReference = payment?.providerReference == null ? null : String(payment.providerReference);
  const canonicalMbossReference = payment?.transid != null
    ? String(payment.transid)
    : (payment?.transactionReference == null ? null : String(payment.transactionReference));
  const checkoutTxnRef = payment?.checkoutFields?.txn_ref == null ? null : String(payment.checkoutFields.txn_ref);
  const invocationCount = diagnostics.filter((entry) => entry.event === 'interswitch.webpayCheckout'
    && entry.attemptId === attemptId && entry.providerReference === providerReference).length;
  const entry = {
    event: lifecycle === 'invocation' ? 'interswitch.webpayCheckout' : 'interswitch.lifecycle',
    lifecycle,
    attemptId,
    canonicalMbossReference,
    providerReference,
    checkoutTxnRef,
    timestamp: new Date().toISOString(),
    invocationCount: lifecycle === 'invocation' ? invocationCount + 1 : invocationCount,
    componentInstanceId,
  };
  if (payment?.purchaseId != null) entry.purchaseId = String(payment.purchaseId);
  diagnostics.push(entry);
  while (diagnostics.length > 100) diagnostics.shift();
  window.__mbossPaymentDiagnostics = diagnostics;
  return entry;
}

function interswitchCheckoutKey(payment) {
  if (payment?.attemptId == null || payment?.providerReference == null) return null;
  return `${String(payment.attemptId)}:${String(payment.providerReference)}`;
}

function hasOpenedInterswitchCheckout(key) {
  if (!key) return false;
  if (interswitchCheckoutKeys.has(key)) return true;
  try {
    return window.sessionStorage.getItem(`mboss.interswitch.checkout:${key}`) === 'opened';
  } catch (_) {
    return false;
  }
}

function markInterswitchCheckoutOpened(key) {
  if (!key) return;
  interswitchCheckoutKeys.add(key);
  try {
    window.sessionStorage.setItem(`mboss.interswitch.checkout:${key}`, 'opened');
  } catch (_) {
    // The module-level key still protects remounts when sessionStorage is unavailable.
  }
}

function loadScript(src) {
  if (scripts[src] && document.querySelector(`script[src="${src}"]`)) return scripts[src];
  delete scripts[src];
  scripts[src] = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) { existing.addEventListener("load", resolve); existing.addEventListener("error", reject); return; }
    const script = document.createElement("script");
    script.src = src; script.async = true; script.onload = resolve; script.onerror = reject;
    document.body.appendChild(script);
  });
  return scripts[src];
}

const INTERSWITCH_INLINE_FIELDS = [
  "merchant_code",
  "pay_item_id",
  "txn_ref",
  "amount",
  "currency",
  "cust_email",
  "mode",
  "site_redirect_url",
];
const INTERSWITCH_INLINE_MODES = new Set(["TEST", "LIVE"]);
// Interswitch approval codes, matching api/providers/interswitch/normalize.js.
const INTERSWITCH_SUCCESS_CODES = new Set(["00", "0", "T0"]);

function interswitchCompletionCode(response) {
  const code = response?.resp ?? response?.ResponseCode ?? response?.responseCode;
  return code == null ? '' : String(code).trim().toUpperCase();
}

function isValidInterswitchInline(payment) {
  if (!payment) return false;
  const fields = payment.checkoutFields;
  if (!fields || typeof fields !== "object" || Array.isArray(fields)) return false;
  if (Object.keys(fields).length !== INTERSWITCH_INLINE_FIELDS.length) return false;
  if (!INTERSWITCH_INLINE_MODES.has(fields.mode)) return false;
  const providerReference = payment.providerReference == null ? "" : String(payment.providerReference);
  if (!providerReference.trim()) return false;
  return INTERSWITCH_INLINE_FIELDS.every((name) => Object.prototype.hasOwnProperty.call(fields, name)
    && (typeof fields[name] === "string" || typeof fields[name] === "number")
    && String(fields[name]).trim() !== '')
    && String(fields.txn_ref) === providerReference;
}

function InlineButton({ provider, payment, onPaid, onClose, autoOpen }) {
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);
  const opened = useRef(false);
  const fields = payment.checkoutFields || {};
  const interswitchKey = interswitchCheckoutKey(payment);
  const previousInterswitchKey = useRef(interswitchKey);
  const componentInstanceId = useRef(nextInterswitchComponentInstanceId++).current;
  const paymentRef = useRef(payment);
  paymentRef.current = payment;
  const source = provider === "provider.flutterwave"
    ? "https://checkout.flutterwave.com/v3.js"
    : provider === "provider.interswitch"
      ? (String(fields.mode).toUpperCase() === "LIVE" ? "https://newwebpay.interswitchng.com/inline-checkout.js" : "https://newwebpay-sandbox.interswitchng.com/inline-checkout.js")
      : "https://checkout.squadco.com/widget/squad.min.js";

  useEffect(() => {
    if (provider === "provider.interswitch" && previousInterswitchKey.current !== interswitchKey) {
      opened.current = false;
    }
    previousInterswitchKey.current = interswitchKey;
  }, [interswitchKey, provider]);

  useEffect(() => {
    if (provider !== "provider.interswitch") return undefined;
    recordInterswitchDiagnostic(paymentRef.current, componentInstanceId, 'mount');
    return () => recordInterswitchDiagnostic(paymentRef.current, componentInstanceId, 'unmount');
  }, [componentInstanceId, provider]);

  useEffect(() => {
    loadScript(source)
      .then(() => setReady(true))
      .catch(() => setError("The provider checkout could not be loaded."));
  }, [source]);

  const open = useCallback(() => {
    if (opened.current || (provider === "provider.interswitch" && hasOpenedInterswitchCheckout(interswitchKey))) return;
    opened.current = true;
    if (provider === "provider.flutterwave" && window.FlutterwaveCheckout) {
      // FlutterwaveCheckout returns a modal handle whose close() dismisses
      // the provider iframe overlay — the provider never closes it for us.
      const modal = window.FlutterwaveCheckout({ ...fields, callback: (response) => {
        // onPaid first: verify() sets `verifying` synchronously, so the
        // provider onclose raised by close() below is absorbed by the
        // verifying/paid guard instead of unwinding the in-flight success.
        onPaid(response?.tx_ref || fields.tx_ref);
        modal?.close?.();
      }, onclose: () => { opened.current = false; if (onClose) onClose(); } });
    } else if (provider === "provider.interswitch" && window.webpayCheckout) {
      recordInterswitchDiagnostic(payment, componentInstanceId, 'invocation');
      markInterswitchCheckoutOpened(interswitchKey);
      window.webpayCheckout({ ...fields, onComplete: (response) => {
        const code = interswitchCompletionCode(response);
        if (code && !INTERSWITCH_SUCCESS_CODES.has(code)) {
          // webpayCheckout exposes no cancel callback: onComplete fires for
          // approved and non-approved terminations alike. An explicit
          // non-approved code is a cancel — unwind the flow, never the
          // success path. An absent/indeterminate code still goes through
          // the common verify path, which remains the truthful arbiter.
          opened.current = false;
          if (onClose) onClose();
          return;
        }
        onPaid(response?.txn_ref || fields.txn_ref);
      } });
    } else if (provider === "provider.squad" && window.squad) {
      const instance = new window.squad({ ...fields, onSuccess: (response) => onPaid(response?.transaction_ref || fields.transaction_ref), onClose: () => { opened.current = false; if (onClose) onClose(); } });
      instance.setup(); instance.open();
    } else { setError("The provider checkout is not available."); opened.current = false; }
  }, [fields, interswitchKey, onClose, onPaid, payment, componentInstanceId, provider]);

  useEffect(() => {
    if (autoOpen && ready && !opened.current) {
      open();
    }
  }, [autoOpen, open, ready]);
  if (error) return <p className="text-danger mb-0">{error}</p>;
  return <button type="button" className="btn btn-primary" disabled={!ready} onClick={open}>{ready ? "Open checkout" : "Loading checkout..."}</button>;
}

export default function PaymentCheckout({ payment, data, publicKey, onPaid, onRefresh, onClose, autoOpen = false }) {
  const checkout = payment || data || {};
  const resolvedKey = publicKey || checkout.publicKey;
  const handlePaid = onPaid || onRefresh || (() => {});
  const handleClose = onClose || (() => {});
  const providerKey = checkout.providerKey || (resolvedKey ? "provider.paystack" : null);
  if (providerKey === "provider.interswitch") {
    if (!isValidInterswitchInline(checkout)) {
      return <p className="text-warning mb-0">Online payment is not available. Please contact support.</p>;
    }
    return <InlineButton provider={providerKey} payment={checkout} onPaid={handlePaid} onClose={handleClose} autoOpen={autoOpen} />;
  }
  if (checkout.checkoutMode === "inline" && checkout.checkoutFields && (providerKey === "provider.flutterwave" || providerKey === "provider.squad")) {
    return <InlineButton provider={providerKey} payment={checkout} onPaid={handlePaid} onClose={handleClose} autoOpen={autoOpen} />;
  }
  if (providerKey === "provider.paystack" && resolvedKey) {
    return <PaystackBtn data={checkout} publicKey={resolvedKey} onPaid={handlePaid} onClose={handleClose} autoOpen={autoOpen} />;
  }
  if (checkout.checkoutUrl) {
    return <a className="btn btn-primary" href={checkout.checkoutUrl} target="_blank" rel="noreferrer">Continue securely</a>;
  }
  return <p className="text-warning mb-0">Online payment is not available. Please contact support.</p>;
}

PaymentCheckout.propTypes = { payment: PropTypes.object, data: PropTypes.object, publicKey: PropTypes.string, onPaid: PropTypes.func, onRefresh: PropTypes.func, onClose: PropTypes.func, autoOpen: PropTypes.bool };
